/* eslint-disable max-lines */
import { Fetcher } from '@this/src/util';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Button } from '@this/shared/ui/inputs/button';
import { styled } from '@this/constants/themes';
import type User from '@this/domain/user/user';
import type { ReceiptCreationActionCreateResponse } from '@this/src/types/receipt_creation_action';

import { Input } from '@this/components/shared/ui/inputs/input';
import Notification from '@this/src/notification';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { media } from '@this/components/shared/atoms/media';

const API_PATHS = {
  NEW: '/trip_receipts/new.json',
  CREATE: '/trip_receipts'
};

const REDIRECT_PATH = '/trip_receipts';

const MESSAGES = {
  ISSUE_SUCCESS: '領収書発行を開始しました。処理完了まで時間がかかる場合があります。',
  COMMUNICATION_ERROR: '通信環境が不安定です。\n時間をおいてもう一度お試しください。',
  EMAIL_REQUIRED: 'メールアドレスを入力してください。',
  INVALID_TRIP_IDS: '有効な旅程IDと請求書IDのペアがありません。領収書発行が可能な旅程を選択してください。'
};

const FORM = {
  DEFAULT_PRICE_REMARKS_TYPE: 'travel_expenses',
  PRICE_REMARKS_TYPES: {
    TRAVEL: 'travel_expenses',
    SALES: 'sales_expenses'
  },
  PLACEHOLDERS: {
    EMAIL: 'メールアドレスを入力してください',
    NAME: '宛名を入力してください'
  },
  BACK_LINK_TEXT: '< 戻る',
  SUBMIT_BUTTON_TEXT: '領収書を発行する'
};

const UI_TEXT = {
  TITLE: '領収書発行',
  TRIP_ID: '旅程番号',
  DESTINATION: '送信先',
  EMAIL: 'メールアドレス',
  RECEIPT_INFO: '領収書情報',
  NAME: '宛名',
  PRICE_BREAKDOWN: '料金の内訳',
  REMARKS: '但書',
  BREAKDOWN_OPTIONS: {
    NONE: 'なし',
    INCLUDE: 'あり'
  },
  REMARKS_OPTIONS: {
    TRAVEL: '出張代金として',
    SALES: '営業交通費として'
  }
};

interface Props {
  user: User | null;
  tripReceiptAvailable: boolean;
}

type Response = {
  trip_invoice_pairs: {
    trip_id: number;
    invoice_id: number | null;
    is_bill_payment: boolean;
  }[];
};

const TripReceiptForm: React.FC<Props> = ({ user, tripReceiptAvailable }) => {
  const location = useLocation();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const isProcessingRef = useRef(false);

  const searchParams = new URLSearchParams(location.search);
  const rawParamValue = searchParams.get('trip_ids');
  const rawTripIds = rawParamValue?.split(',').filter(id => id.trim() !== '') || [];

  const [tripInvoicePairs, setTripInvoicePairs] = useState<
    { trip_id: number; invoice_id: number | null; is_bill_payment: boolean }[]
  >([]);

  // 有効なtrip_idsのみをフィルタリング
  const validTripIds = useMemo(() => {
    return tripInvoicePairs
      .filter(pair => {
        // is_bill_payment=true の場合は invoice_id が必要、それ以外はOK
        if (pair.is_bill_payment) {
          return typeof pair.invoice_id === 'number' && pair.invoice_id > 0;
        }
        return true;
      })
      .map(pair => `${pair.trip_id}-${pair.invoice_id ?? ''}`);
  }, [tripInvoicePairs]);

  // APIリクエスト済みのIDを記録するRef
  const processedIdsRef = useRef<Set<string>>(new Set());

  // 表示用のtrip_id（ハイフン以降を除去）
  const displayTripIds = useMemo(() => {
    return tripInvoicePairs
      .filter(pair => {
        if (pair.is_bill_payment) {
          return typeof pair.invoice_id === 'number' && pair.invoice_id > 0;
        }
        return true;
      })
      .map(pair => String(pair.trip_id));
  }, [tripInvoicePairs]);

  const [hasValidTripIds, setHasValidTripIds] = useState(validTripIds.length > 0);

  const [email, setEmail] = useState(user?.email || '');
  const [name, setName] = useState(user?.organization?.name || '');
  const [includePriceBreakdown, setIncludePriceBreakdown] = useState(false);
  const [priceRemarksType, setPriceRemarksType] = useState(FORM.DEFAULT_PRICE_REMARKS_TYPE);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const setProcessingState = (state: boolean) => {
    setIsProcessing(state);
    isProcessingRef.current = state;
  };

  useEffect(() => {
    const hasValid = tripInvoicePairs.some(pair => {
      return pair.is_bill_payment ? typeof pair.invoice_id === 'number' && pair.invoice_id > 0 : true;
    });
    setHasValidTripIds(hasValid);
  }, [tripInvoicePairs]);

  // 領収書オプションのチェック
  useEffect(() => {
    if (rawTripIds.length === 0) {
      setIsLoading(false);
      return;
    }

    const unprocessedTripIds = rawTripIds.filter(id => !processedIdsRef.current.has(id));

    if (unprocessedTripIds.length === 0) {
      setIsLoading(false);
      return;
    }

    Fetcher.get<Response>(API_PATHS.NEW, { trip_ids: unprocessedTripIds })
      .then(res => {
        if (res && res.trip_invoice_pairs) {
          setTripInvoicePairs(res.trip_invoice_pairs);
        }
        unprocessedTripIds.forEach(id => processedIdsRef.current.add(id));
      })
      .catch(e => {
        if (isMountedRef.current) {
          const errors =
            e.response.data && e.response.data.errors ? e.response.data.errors : [MESSAGES.COMMUNICATION_ERROR];

          unprocessedTripIds.forEach(id => processedIdsRef.current.add(id));

          setHasValidTripIds(false);

          setTimeout(() => {
            if (isMountedRef.current) {
              Notification.error(errors);
            }
          }, 0);
        }
      })
      .finally(() => {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      });
  }, [tripReceiptAvailable, rawTripIds]);

  const handleSubmit = () => {
    if (isProcessingRef.current) {
      return;
    }

    setError('');

    if (!hasValidTripIds) {
      Notification.error(MESSAGES.INVALID_TRIP_IDS);
      return;
    }

    if (!email.trim()) {
      setError(MESSAGES.EMAIL_REQUIRED);
      return;
    }

    setIsLoading(true);
    setProcessingState(true);
    const params = {
      trip_ids: validTripIds,
      email,
      name,
      include_price_breakdown: includePriceBreakdown,
      price_remarks_type: priceRemarksType
    };

    Fetcher.post<ReceiptCreationActionCreateResponse>(API_PATHS.CREATE, params)
      .then((response: ReceiptCreationActionCreateResponse) => {
        if (!isMountedRef.current) return;

        if (!response) {
          throw new Error(MESSAGES.COMMUNICATION_ERROR);
        }

        Notification.success(MESSAGES.ISSUE_SUCCESS);
        setProcessingState(false);
        history.push(REDIRECT_PATH);
      })
      .catch(e => {
        if (!isMountedRef.current) return;

        const errors =
          e.response?.data?.error ||
          (e.response?.data?.errors ? e.response.data.errors : [MESSAGES.COMMUNICATION_ERROR]);
        Notification.error(errors);
        setProcessingState(false);
      })
      .finally(() => {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      });
  };

  return (
    <Container isLoading={isLoading}>
      <Title>{UI_TEXT.TITLE}</Title>
      {isLoading && (
        <LoadingOverlay>
          <Loading loading />
        </LoadingOverlay>
      )}

      <Section>
        <SubTitle>{UI_TEXT.TRIP_ID}</SubTitle>
        <ChildContent>
          {hasValidTripIds ? (
            <TripIds>{displayTripIds.join(', ')}</TripIds>
          ) : (
            <ErrorTripIds>{MESSAGES.INVALID_TRIP_IDS}</ErrorTripIds>
          )}
        </ChildContent>
      </Section>

      <Section>
        <SubTitle>{UI_TEXT.DESTINATION}</SubTitle>
        <ChildContent>
          <Label>
            {UI_TEXT.EMAIL} <span className="required">*</span>
          </Label>
          <div>
            <Input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={FORM.PLACEHOLDERS.EMAIL}
              size="medium"
              style={{ width: '400px', maxWidth: '100%' }}
            />
            {error && <ErrorText>{error}</ErrorText>}
          </div>
        </ChildContent>
      </Section>

      <Section>
        <SubTitle>{UI_TEXT.RECEIPT_INFO}</SubTitle>
        <ChildContent>
          <Label>{UI_TEXT.NAME}</Label>
          <Input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={FORM.PLACEHOLDERS.NAME}
            size="medium"
            style={{ width: '400px', maxWidth: '100%' }}
          />
        </ChildContent>

        <ChildContent>
          <Label>{UI_TEXT.PRICE_BREAKDOWN}</Label>
          <RadioGroup>
            <RadioLabel>
              <input
                type="radio"
                checked={!includePriceBreakdown}
                onChange={() => setIncludePriceBreakdown(false)}
              />
              {UI_TEXT.BREAKDOWN_OPTIONS.NONE}
            </RadioLabel>
            <RadioLabel>
              <input
                type="radio"
                checked={includePriceBreakdown}
                onChange={() => setIncludePriceBreakdown(true)}
              />
              {UI_TEXT.BREAKDOWN_OPTIONS.INCLUDE}
            </RadioLabel>
          </RadioGroup>
        </ChildContent>

        <ChildContent>
          <Label>{UI_TEXT.REMARKS}</Label>
          <RadioGroup>
            <RadioLabel>
              <input
                type="radio"
                checked={priceRemarksType === FORM.PRICE_REMARKS_TYPES.TRAVEL}
                onChange={() => setPriceRemarksType(FORM.PRICE_REMARKS_TYPES.TRAVEL)}
              />
              {UI_TEXT.REMARKS_OPTIONS.TRAVEL}
            </RadioLabel>
            <RadioLabel>
              <input
                type="radio"
                checked={priceRemarksType === FORM.PRICE_REMARKS_TYPES.SALES}
                onChange={() => setPriceRemarksType(FORM.PRICE_REMARKS_TYPES.SALES)}
              />
              {UI_TEXT.REMARKS_OPTIONS.SALES}
            </RadioLabel>
          </RadioGroup>
        </ChildContent>
      </Section>

      <ButtonContainer>
        <ConfirmBack onClick={() => history.goBack()}>{FORM.BACK_LINK_TEXT}</ConfirmBack>
        <Button
          color="primary"
          size="medium"
          loading={isLoading}
          disabled={isLoading || !hasValidTripIds || isProcessing}
          onClick={handleSubmit}
        >
          {FORM.SUBMIT_BUTTON_TEXT}
        </Button>
      </ButtonContainer>
    </Container>
  );
};

const Container = styled.div<{ isLoading: boolean }>`
  min-height: 85vh;
  max-width: 1150px;
  width: 90%;
  margin: 20px auto;
  padding: 20px;
  background: #fff;
  position: relative;

  ${({ isLoading }) =>
    isLoading &&
    `
    opacity: 0.5;
    pointer-events: none;
  `}
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 10;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 30px;
  ${media.sp`
    margin-bottom: 20px;
  `}
`;

const Section = styled.div`
  margin-bottom: 30px;
  ${media.sp`
    margin-bottom: 20px;
  `}
`;

const SubTitle = styled.div`
  font-size: 15px;
  font-weight: bold;
  word-wrap: break-word;
`;

const Label = styled.div`
  font-size: 13px;
  word-wrap: break-word;
  width: 200px;
  display: flex;
  align-items: center;
  & .required {
    padding-left: 4px;
    font-size: 15px;
    font-weight: bold;
    color: red;
  }
  ${media.sp`
    width: 100%;
  `}
`;

const TripIds = styled.div`
  font-size: 13px;
  word-wrap: break-word;
`;

const ErrorTripIds = styled.div`
  font-size: 13px;
  word-wrap: break-word;
  color: red;
`;

const ChildContent = styled.div`
  display: flex;
  gap: 20px;
  padding: 10px 20px;
  ${media.sp`
    flex-direction: column;
    gap: 4px;
    padding: 8px 0 0 10px;
  `}
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 10px;
  font-size: 13px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 2px;
`;

const ErrorText = styled.p`
  color: red;
  font-size: 13px;
  margin-top: 5px;
`;

const ButtonContainer = styled.div`
  position: absolute;
  bottom: 20px;
  left: 0;
  display: flex;
  gap: 20px;
  justify-content: flex-start;
  padding-left: 20px;
  ${media.sp`
    justify-content: center;
    bottom: 10px;
  `}
`;

const ConfirmBack = styled.a`
  cursor: pointer;
  margin: auto 0;
  font-size: 12px;
  font-weight: bold;
`;

export default TripReceiptForm;
